/**
 * The Awards card body mounted in pool Settings' reserved `awards` slot.
 * Fetches `/award/custom-list`, runs it through `buildPoolAwardsModel`, and
 * renders the two kind sections with their counters, rows and Create
 * controls, plus the collapsed shared Archived section.
 *
 * The list query is mounted with `{ staleTime: 0, refetchOnMount: "always" }`
 * because `useAwardCustomGive` and `useAwardCustomTakeBack` invalidate only
 * `awardGetForMember`, not this card's `awardCustomList` key, so the counts
 * would otherwise go stale after a give from the give-award sheet.
 */
import {
  useAwardCustomList,
  useAwardCustomRestore,
} from "@sportsmet/contest-sdk/private";
import { Button, InlineBanner, Skeleton } from "@sportsmet/ui";
import { type ReactNode, useState } from "react";

import { extractErrorCode, showApiError } from "../../errors/apiErrorBus";
import { AwardTypeRow } from "./AwardTypeRow";
import {
  AWARDS_CARD_ARCHIVED_LABEL,
  AWARDS_FAILED_MESSAGE,
  AWARDS_LOADING_LABEL,
  AWARDS_RETRY_LABEL,
  awardsCapReasonLabel,
  awardsCounterLabel,
  awardsCreateLabel,
  kindLabelPlural,
  type typeAwardKind,
} from "./poolAwardsCopy";
import {
  buildPoolAwardsModel,
  type typeAwardKindSection,
  type typeAwardTypeRow,
} from "./poolAwardsModel";

// The active-row and archived-row lists share one <AwardTypeRow>. A row's
// unused control (Restore on an active row, Remove on an archived row) still
// needs a callback prop; this no-op fills that slot without an empty arrow
// function body.
function NOOP(): undefined {
  return undefined;
}

export type typePoolAwardsCardProps = {
  poolId: string;
  onCreate?: ((kind: typeAwardKind) => void) | undefined;
  onOpenEdit?: ((awardTypeId: string) => void) | undefined;
  onRemove?: ((awardTypeId: string) => void) | undefined;
};

export const PoolAwardsCard = ({
  poolId,
  onCreate,
  onOpenEdit,
  onRemove,
}: typePoolAwardsCardProps): ReactNode => {
  const [isArchivedOpen, setIsArchivedOpen] = useState(false);
  const list = useAwardCustomList(
    { poolId },
    { staleTime: 0, refetchOnMount: "always" },
  );
  const restore = useAwardCustomRestore();

  if (list.isLoading) {
    return (
      <div data-testid="pool-awards-card">
        <Skeleton shape="card" label={AWARDS_LOADING_LABEL} />
        <Skeleton shape="card" label={AWARDS_LOADING_LABEL} />
        <Skeleton shape="card" label={AWARDS_LOADING_LABEL} />
      </div>
    );
  }

  if (list.isError || extractErrorCode(list.data) !== null) {
    return (
      <div data-testid="pool-awards-card">
        <InlineBanner
          variant="warning"
          message={AWARDS_FAILED_MESSAGE}
          action={{
            label: AWARDS_RETRY_LABEL,
            onAction: () => {
              void list.refetch();
            },
          }}
        />
      </div>
    );
  }

  const medals = (list.data?.data?.medals ?? []) as readonly typeAwardTypeRow[];
  const trophies = (list.data?.data?.trophies ??
    []) as readonly typeAwardTypeRow[];
  const model = buildPoolAwardsModel({ medals, trophies });
  const isBusy = restore.isPending;

  async function handleRestore(awardTypeId: string): Promise<void> {
    const result = await restore.mutateAsync({ poolId, awardTypeId });
    if (showApiError(result)) return;
  }

  function renderSection(section: typeAwardKindSection): ReactNode {
    return (
      <section key={section.kind} className="flex flex-col gap-2">
        <h4 className="text-h4 text-ink">
          {kindLabelPlural(section.kind)}{" "}
          <span className="text-ink-secondary">
            {awardsCounterLabel(section.activeCount)}
          </span>
        </h4>
        <ul className="flex flex-col divide-y divide-line">
          {section.activeRows.map((row) => (
            <AwardTypeRow
              key={row.awardTypeId}
              row={row}
              kind={section.kind}
              variant="active"
              isBusy={isBusy}
              onOpenEdit={(id) => onOpenEdit?.(id)}
              onRemove={(id) => onRemove?.(id)}
              onRestore={NOOP}
            />
          ))}
        </ul>
        <Button
          variant="ghost"
          size="md"
          fullWidth
          data-testid={`award-create-${section.kind}`}
          disabled={!section.canCreate}
          onClick={() => {
            onCreate?.(section.kind);
          }}
        >
          {awardsCreateLabel(section.kind)}
        </Button>
        {!section.canCreate ? (
          <p
            className="text-caption text-ink-secondary"
            data-testid={`award-create-reason-${section.kind}`}
          >
            {awardsCapReasonLabel(section.kind)}
          </p>
        ) : null}
      </section>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="pool-awards-card">
      {renderSection(model.medals)}
      {renderSection(model.trophies)}
      {model.hasArchived ? (
        <section className="flex flex-col gap-2">
          <button
            type="button"
            data-testid="award-archived-toggle"
            aria-expanded={isArchivedOpen}
            className="text-left text-body-strong text-ink"
            onClick={() => {
              setIsArchivedOpen((open) => !open);
            }}
          >
            {AWARDS_CARD_ARCHIVED_LABEL} · {model.archived.length}
          </button>
          {isArchivedOpen ? (
            <ul className="flex flex-col divide-y divide-line">
              {model.archived.map((entry) => (
                <AwardTypeRow
                  key={entry.row.awardTypeId}
                  row={entry.row}
                  kind={entry.kind}
                  variant="archived"
                  canRestore={entry.canRestore}
                  isBusy={isBusy}
                  onOpenEdit={(id) => onOpenEdit?.(id)}
                  onRemove={NOOP}
                  onRestore={(id) => {
                    void handleRestore(id);
                  }}
                />
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}
    </div>
  );
};
